import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import {
    ClosureAnnouncementBarInner,
    ClosureAnnouncementBarRoot,
    ClosureAnnouncementBarText,
    ClosureAnnouncementCloseIcon,
    ClosureAnnouncementModalBackdrop,
    ClosureAnnouncementModalClose,
    ClosureAnnouncementModalDescription,
    ClosureAnnouncementModalMailLink,
    ClosureAnnouncementModalPanel,
    ClosureAnnouncementModalSectionHeading,
    ClosureAnnouncementModalTitle,
    ClosureAnnouncementReadMore,
    ClosureAnnouncementReveal,
    ClosureAnnouncementRevealInner,
} from './ClosureAnnouncementTopBar.style';

const BANNER_REVEAL_DELAY_MS = 1000;

const ClosureAnnouncementModalContent = (props: {
    accountHelperMail: string;
    operatorFullName: string;
    onClose: () => void;
}): JSX.Element => {
    const { accountHelperMail, operatorFullName, onClose } = props;

    return (
        <ClosureAnnouncementModalPanel
            aria-labelledby='closure-announcement-modal-title'
            aria-modal='true'
            data-test='closure-announcement-modal-panel'
            onClick={(e): void => {
                e.stopPropagation();
            }}
            role='dialog'
        >
            <ClosureAnnouncementModalClose
                aria-label='Close'
                data-test='closure-announcement-modal-close'
                onClick={onClose}
                type='button'
            >
                <ClosureAnnouncementCloseIcon />
            </ClosureAnnouncementModalClose>
            <ClosureAnnouncementModalTitle id='closure-announcement-modal-title'>
                <I18n
                    langKey='account.closure-modal.title'
                    defaultText='{operatorFullName} has Closed'
                    params={{ operatorFullName }}
                />
            </ClosureAnnouncementModalTitle>
            <ClosureAnnouncementModalDescription>
                <I18n
                    langKey='account.closure-modal.description-part-1'
                    defaultText='After careful consideration, we made the difficult decision to cease operations on Wednesday, 20 May 2026. We want to take this opportunity to sincerely thank all of our customers for their loyalty and support.'
                />
            </ClosureAnnouncementModalDescription>
            <ClosureAnnouncementModalDescription>
                <I18n
                    langKey='account.closure-modal.description-part-2'
                    defaultText='Please take a moment to read the important information below.'
                />
            </ClosureAnnouncementModalDescription>
            <ClosureAnnouncementModalSectionHeading>
                <I18n
                    langKey='account.closure-modal.section-withdraw-heading'
                    defaultText='Withdrawal of Funds'
                />
            </ClosureAnnouncementModalSectionHeading>
            <ClosureAnnouncementModalDescription>
                <I18n
                    langKey='account.closure-modal.withdraw-description'
                    defaultText='If you have any funds remaining in your {operatorFullName} account, please reach out to us. We can promptly return all funds once you have verified you are the valid account holder.'
                    params={{ operatorFullName }}
                />
            </ClosureAnnouncementModalDescription>
            <ClosureAnnouncementModalSectionHeading>
                <I18n
                    langKey='account.closure-modal.section-bets-heading'
                    defaultText='Open & Ante-Post Bets'
                />
            </ClosureAnnouncementModalSectionHeading>
            <ClosureAnnouncementModalDescription>
                <I18n
                    langKey='account.closure-modal.bets-description'
                    defaultText='We will honour all open and ante-post bets placed before the closure date. Any winnings that settle after this date, will be manually sent to your primary payment method. If you would like a copy of your open bets, or if there are any issues or concerns, please speak to us.'
                />
            </ClosureAnnouncementModalDescription>
            <ClosureAnnouncementModalSectionHeading>
                <I18n
                    langKey='account.closure-modal.section-support-heading'
                    defaultText="Questions? We're Here to Help"
                />
            </ClosureAnnouncementModalSectionHeading>
            <ClosureAnnouncementModalDescription>
                <I18n
                    langKey='account.closure-modal.support-part-1'
                    defaultText='Our customer support team remains available to assist you with any queries or concerns. Please reach out to us at'
                />{' '}
                <ClosureAnnouncementModalMailLink href={`mailto:${accountHelperMail}`}>
                    <I18n
                        langKey='account.closure-modal.info-page.email'
                        defaultText='{accountHelperMail}'
                        params={{ accountHelperMail }}
                    />
                </ClosureAnnouncementModalMailLink>{' '}
                <I18n
                    langKey='account.closure-modal.support-part-2'
                    defaultText='and we will respond as promptly as possible.'
                />
            </ClosureAnnouncementModalDescription>
            <ClosureAnnouncementModalDescription>
                <I18n
                    langKey='account.closure-modal.thank-you'
                    defaultText='Thank you once again for being part of the {operatorFullName} community.'
                    params={{ operatorFullName }}
                />
            </ClosureAnnouncementModalDescription>
            <ClosureAnnouncementModalDescription>
                <I18n
                    langKey='account.closure-modal.signature'
                    defaultText='The {operatorFullName} Team'
                    params={{ operatorFullName }}
                />
            </ClosureAnnouncementModalDescription>
        </ClosureAnnouncementModalPanel>
    );
};

export const ClosureAnnouncementTopBar = observer('ClosureAnnouncementTopBar', (): JSX.Element => {
    const common = useCommon();
    const env = EnvironmentState.get(common);
    const { accountHelperMail, operatorFullName } = ConfigComponents.get(common).config;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBarVisible, setIsBarVisible] = useState(false);

    useEffect(() => {
        const showTimer = window.setTimeout((): void => {
            setIsBarVisible(true);
        }, BANNER_REVEAL_DELAY_MS);

        return (): void => {
            window.clearTimeout(showTimer);
        };
    }, []);

    useEffect(() => {
        if (isModalOpen === false) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setIsModalOpen(false);
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isModalOpen]);

    const modal =
        isModalOpen === true && env.isBrowser === true
            ? createPortal(
                  <ClosureAnnouncementModalBackdrop
                      data-test='closure-announcement-modal-backdrop'
                      onClick={(): void => {
                          setIsModalOpen(false);
                      }}
                  >
                      <ClosureAnnouncementModalContent
                          accountHelperMail={accountHelperMail}
                          operatorFullName={operatorFullName}
                          onClose={(): void => {
                              setIsModalOpen(false);
                          }}
                      />
                  </ClosureAnnouncementModalBackdrop>,
                  document.body
              )
            : null;

    return (
        <>
            <ClosureAnnouncementReveal visible={isBarVisible}>
                <ClosureAnnouncementRevealInner>
                    <ClosureAnnouncementBarRoot visible={isBarVisible}>
                        <ClosureAnnouncementBarInner>
                            <ClosureAnnouncementBarText>
                                <I18n
                                    langKey='account.closure-banner.message'
                                    defaultText='NRG has now closed. '
                                />{' '}
                                <ClosureAnnouncementReadMore
                                    data-test='closure-announcement-read-more'
                                    onClick={(): void => {
                                        setIsModalOpen(true);
                                    }}
                                    type='button'
                                >
                                    <I18n
                                        langKey='account.closure-banner.read-more'
                                        defaultText='Click here for more information.'
                                    />
                                </ClosureAnnouncementReadMore>
                            </ClosureAnnouncementBarText>
                        </ClosureAnnouncementBarInner>
                    </ClosureAnnouncementBarRoot>
                </ClosureAnnouncementRevealInner>
            </ClosureAnnouncementReveal>
            {modal}
        </>
    );
});
